import * as React from "react";
import {
  Input,
  Dropdown,
  Option,
  makeStyles,
  shorthands,
  useId,
  Toast,
  ToastTitle,
  Toaster,
  useToastController,
} from "@fluentui/react-components";
import FormHeader from "./FormHeader";
import { useLocation } from "react-router";
import { Risiko } from "./RisikenTabelleDataGrid";
import { apiUrl } from "../utils/api";

const useStyles = makeStyles({
  root: {
    ...shorthands.padding("20px"),
    display: "flex",
    flexDirection: "column",
    gap: "32px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px 80px",
    alignItems: "center",
  },
  fieldRow: {
    display: "grid",
    gridTemplateColumns: "45% 55%",
    alignItems: "center",
    gap: "8px",
    minHeight: "40px",
  },
  label: {
    // entferne minWidth/maxWidth, damit es sich in der 45%-Spalte weiten kann:
    textAlign: "left",
    paddingRight: "8px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    fontWeight: 400,
    fontSize: "16px",
    letterSpacing: "1px",
    position: "relative",
    "&::after": {
      content:
        "'........................................................................'",
      left: "100%",
      top: "0%",
      color: "#ccc",
      fontSize: "16px",
      pointerEvents: "none",
      whiteSpace: "nowrap",
      width: "100%",
      overflow: "hidden",
    },
  },
  inputContainer: {
    width: "100%",
  },
  inputFullWidth: {
    width: "100%",
  },
});

export interface RisikoFormValues {
  risikoID?: string;
  name?: string;
  risikoobjekt?: string;
  zweig?: string;
  vertrag?: string;
}

interface RisikoFormProps {
  initialValues?: Risiko;
}

const zweigOptions = [
  "Automatenversicherung",
  "Ausstellungsversicherung",
  "Bauleistungsversicherung",
  "Haftpflichtversicherung",
  // ... weitere Versicherungsarten
];
const vertragOptions = ["V12345", "V67890"]; // Beispiel

export const RisikoForm: React.FC<RisikoFormProps> = ({
  initialValues = {} as Risiko,
}) => {
  const styles = useStyles();
  const idZweig = useId();
  const idVertrag = useId();
  const toasterId = useId("risiko-form-toaster");
  const { dispatchToast } = useToastController(toasterId);

  const location = useLocation();
  initialValues = location.state?.risks || ({} as Risiko);

  const [risikoID, setRisikoID] = React.useState(initialValues.RisikoID ?? "");
  const [name, setName] = React.useState(initialValues.KontaktName ?? "");
  const [risikoobjekt, setRisikoobjekt] = React.useState(
    initialValues.RisikoOrt ?? "",
  );
  const [zweig, setZweig] = React.useState<string[]>(
    initialValues.Zweig ? [initialValues.Zweig] : [],
  );
  const [vertrag, setVertrag] = React.useState<string[]>(
    initialValues.Vertragsnummer ? [initialValues.Vertragsnummer] : [],
  );
  const [kontaktID, setKontaktID] = React.useState<number | "">(
    initialValues.KontaktID ?? "",
  );
  const [vertragID, setVertragID] = React.useState<number | "">(
    initialValues.VertragID ?? "",
  );

  const handleSave = async () => {
    const id = Number(risikoID);
    if (!Number.isFinite(id) || id <= 0) {
      dispatchToast(
        <Toast>
          <ToastTitle>Ungültige Risiko-ID</ToastTitle>
        </Toast>,
        { intent: "error" },
      );
      return;
    }

    try {
      const res = await fetch(apiUrl(`risk-objects/${id}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: risikoobjekt,
          riskType: zweig[0] || null,
          contactID: Number.isFinite(Number(kontaktID)) && Number(kontaktID) > 0 ? Number(kontaktID) : null,
          contractID: Number.isFinite(Number(vertragID)) && Number(vertragID) > 0 ? Number(vertragID) : null,
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      dispatchToast(
        <Toast>
          <ToastTitle>Risiko gespeichert</ToastTitle>
        </Toast>,
        { intent: "success" },
      );
    } catch (error) {
      console.error("Failed to save risk object", error);
      dispatchToast(
        <Toast>
          <ToastTitle>Speichern fehlgeschlagen</ToastTitle>
        </Toast>,
        { intent: "error" },
      );
    }
  };

  return (
    <div className={styles.root}>
      <Toaster toasterId={toasterId} position="top-end" />
      <FormHeader
        title={
          risikoobjekt.length > 0
            ? risikoobjekt + ", " + name
            : "Neues Risikoobjekt"
        }
        onSave={handleSave}
      />
      <div className={styles.grid}>
        {/* Risiko-ID */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Risiko-ID:</span>
          <div className={styles.inputContainer}>
            <Input
              value={"" + risikoID}
              placeholder="ID eingeben"
              onChange={(_, data) => setRisikoID(+data.value)}
              className={styles.inputFullWidth}
            />
          </div>
        </div>

        {/* Name */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Name:</span>
          <div className={styles.inputContainer}>
            <Input
              value={name}
              placeholder="Name"
              onChange={(_, data) => setName(data.value)}
              className={styles.inputFullWidth}
            />
          </div>
        </div>

        {/* risikoobjekt */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Risikoobjekt:</span>
          <div className={styles.inputContainer}>
            <Input
              value={risikoobjekt}
              placeholder="Objekt"
              onChange={(_, data) => setRisikoobjekt(data.value)}
              className={styles.inputFullWidth}
            />
          </div>
        </div>

        {/* Zweig */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Zweig:</span>
          <div className={styles.inputContainer}>
            <Dropdown
              clearable
              defaultValue={zweig[0]}
              selectedOptions={zweig}
              onOptionSelect={(_, data) =>
                setZweig(data.optionValue ? [data.optionValue] : [])
              }
              placeholder="Versicherungsart wählen"
              id={idZweig}
              className={styles.inputFullWidth}
            >
              {zweigOptions.map((z) => (
                <Option key={z} value={z}>
                  {z}
                </Option>
              ))}
            </Dropdown>
          </div>
        </div>

        {/* Vertragsnummer */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Vertrag:</span>
          <Dropdown
            clearable
            className={styles.inputFullWidth}
            defaultValue={vertrag[0]}
            selectedOptions={vertrag}
            onOptionSelect={(_, data) =>
              setVertrag(data.optionValue ? [data.optionValue] : [])
            }
            placeholder="Vertrag wählen"
            id={idVertrag}
          >
            {vertragOptions.map((v) => (
              <Option key={v} value={v}>
                {v}
              </Option>
            ))}
          </Dropdown>
        </div>

        <div className={styles.fieldRow}>
          <span className={styles.label}>Kontakt-ID:</span>
          <div className={styles.inputContainer}>
            <Input
              value={kontaktID === "" ? "" : String(kontaktID)}
              placeholder="Kontakt-ID"
              onChange={(_, data) => {
                const next = Number(data.value);
                setKontaktID(data.value.trim() === "" || Number.isNaN(next) ? "" : next);
              }}
              className={styles.inputFullWidth}
            />
          </div>
        </div>

        <div className={styles.fieldRow}>
          <span className={styles.label}>Vertrag-ID:</span>
          <div className={styles.inputContainer}>
            <Input
              value={vertragID === "" ? "" : String(vertragID)}
              placeholder="Vertrag-ID"
              onChange={(_, data) => {
                const next = Number(data.value);
                setVertragID(data.value.trim() === "" || Number.isNaN(next) ? "" : next);
              }}
              className={styles.inputFullWidth}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
